import { useCallback, useEffect, useRef } from 'react'
import { ChatWindow, useAgentRuntime, useAgentStore, type AgentToolDefinition } from '@multiplayer-app/ai-agent-react'
import { AgentAttachmentType, type AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { WRITE_TOOL_NAMES } from '@/shared/agent/tools'
import * as word from '@/shared/office/word'
import { DocumentChatHistory } from './DocumentChatHistory'
import { latestAssistantText, type AgentStoreLike } from './latestAssistantText'

const isPageContext = (attachment: AgentAttachment) =>
  attachment.type === AgentAttachmentType.Context &&
  (attachment as { metadata?: { kind?: string } }).metadata?.kind === 'pageContext'

/**
 * The chat screen: the agent transcript plus the ToolShelf. Selecting a write
 * tool seeds its input with the latest assistant reply so the user can apply a
 * proposed change without retyping it.
 */
export function ChatScreen() {
  const runtime = useAgentRuntime()
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const draftChatKey = useAgentStore((state) => state.draftChatKey)

  // The composer draft is keyed by chat id, so starting a new chat leaves the
  // previous chat's in-progress context attachments behind. Carry them onto the
  // new draft. Skip the page context — the composer rebuilds a fresh one for the
  // new chat (and auto-adds it before this runs, so an emptiness check would
  // wrongly drop the custom attachments); addComposerAttachments dedupes by id.
  const prevComposerKeyRef = useRef<string>()
  useEffect(() => {
    const currentKey = activeChatId ?? draftChatKey
    const prevKey = prevComposerKeyRef.current
    prevComposerKeyRef.current = currentKey
    if (!prevKey || prevKey === currentKey || activeChatId !== undefined) return
    const store = runtime.store.getState()
    const carried = (store.chatState[prevKey]?.composerDraft.attachments ?? []).filter((a) => !isPageContext(a))
    if (carried.length > 0) store.addComposerAttachments(currentKey, carried)
  }, [activeChatId, draftChatKey, runtime])

  const onToolSelected = useCallback(
    async (tool: AgentToolDefinition) => {
      try {
        const input: Record<string, unknown> = {}
        let toolName = tool.name
        if (WRITE_TOOL_NAMES.has(tool.name)) {
          const text = latestAssistantText(runtime.store.getState() as unknown as AgentStoreLike)
          if (text) input.text = text
        }
        if (tool.name === 'insertWordText' || tool.name === 'replaceWordSelection') {
          const selectedText = await word.getSelection()
          if (selectedText) input.expectedSelectionText = selectedText
        } else if (tool.name === 'replaceWordText') {
          // The shelf has no anchor form. Treat the user's live selection as the
          // authoritative target and route through the selection-safe tool.
          const selectedText = await word.getSelection()
          if (!selectedText) throw new Error('Select the Word text to replace first.')
          toolName = 'replaceWordSelection'
          input.expectedSelectionText = selectedText
        } else if (tool.name === 'replaceWordParagraphs') {
          Object.assign(input, await word.getSelectedParagraphRange())
        }
        await runtime.tools.invoke(toolName, input)
      } catch (error) {
        // Most common cause: no active chat yet. Surface to the console; the
        // ToolShelf stays usable once a chat exists.
        console.error(`Office tool "${tool.name}" failed:`, error)
      }
    },
    [runtime]
  )

  return (
    <>
      {/* Start every pane in a fresh session: restoreLastActiveChat={false}
          skips reading the persisted chat id, and loadChats={false} stops the
          bootstrap from auto-selecting the most-recent chat (which otherwise
          flashes another document's conversation before our reset). History is
          reachable from the controls bar. */}
      <ChatWindow onToolSelected={onToolSelected} restoreLastActiveChat={false} loadChats={false} />
      <DocumentChatHistory />
    </>
  )
}
